import { ImageUrlProps } from '../hooks/use-image-url'

export const isAbsoluteUrl = (url: string) =>
  url.startsWith('http://') || url.startsWith('https://')

export const slugify = (text: string) => {
  if (!text) {
    return null
  }

  let slug = text
    .toLowerCase()
    .replace(/[^a-zA-ZäåöÄÅÖ0-9\s-]/g, '')
    .replace(/\s+/g, ' ')

  if (slug.length > 500) {
    slug = slug.substring(0, 500)
  }

  slug = slug.trim().replace(/\s/g, '-').replace(/--+/g, '-')

  return slug
}

export const stripTrailingSlash = (path: string) => path?.replace(/\/$/, '')

export const getMediaUrl = (
  fileId: string | null | undefined,
  textToCreateSlugFrom = '',
  environment = ''
) => {
  if (!fileId) {
    return ''
  }

  const mediaLocationUrl = getMediaLocationUrl(environment)

  const [id, extension] = fileId.split('.')
  const slug = slugify(textToCreateSlugFrom) ?? ''

  return `${mediaLocationUrl}media/${id}/${slug}.${extension}`
}

export const getImageUrl = ({
  imageFilename,
  xSize = 400,
  ySize = 320,
  textToCreateSlugFrom = '',
  environment = '',
}: ImageUrlProps & { environment: string }) => {
  const mediaLocationUrl = getMediaLocationUrl(environment)

  if (!imageFilename) {
    return `${mediaLocationUrl}image/default/${xSize}/${ySize}/default.png`
  }

  // F&O products use full URLs for images
  if (isAbsoluteUrl(imageFilename)) {
    return imageFilename
  }

  // eslint-disable-next-line prefer-const
  const [filenameBase, fileExtension] = imageFilename.split('.')

  if (!fileExtension) {
    imageFilename = imageFilename + '.jpg'
  }
  const slug = slugify(textToCreateSlugFrom)
  const filename = slug ? `${slug}.${fileExtension || 'jpg'}` : imageFilename
  return `${mediaLocationUrl}image/${filenameBase}/${xSize}/${ySize}/${filename}`
}

type PdpTabs = 'description' | 'specification' | 'accessories' | 'reviews'

export const getProductUrl = (
  productErpIdentifier: string,
  nameSlug: string,
  basePath = '/product',
  scrollToTab?: boolean,
  tab?: PdpTabs
) => {
  const queryParams: string[] = []
  if (scrollToTab) {
    queryParams.push(`scrollToTab=${encodeURIComponent('true')}`)
  }
  if (tab) {
    queryParams.push(`tab=${encodeURIComponent(tab)}`)
  }

  const queryString = queryParams.join('&')
  const url = `${basePath}/${productErpIdentifier}/${nameSlug}${
    queryString ? `?${queryString}` : ''
  }`

  return url
}

export const getCategoryUrl = (pathSlug: string | undefined | null) =>
  pathSlug ? `/group/${stripTrailingSlash(pathSlug)}` : ''

const getMediaLocationUrl = (environment: string) => {
  switch (environment) {
    case 'local':
    case 'test':
      return '//test.media.dustin.eu.dustingroup.org/'
    case 'staging':
      return '//stage.media.dustin.eu.dustingroup.org/'
    default:
      return 'https://media.dustin.eu/'
  }
}

/*
 * Only use when served behind Cloudflare cf-images.dustin.eu domain
 */
export const getRelativeProductImageUrl = (
  imageFilename: string | null | undefined,
  xSize = 400,
  ySize = 320,
  textToCreateSlugFrom = ''
) => {
  if (!imageFilename) {
    return 'image/default/200/200/default.png'
  }

  // F&O products use full URLs for images
  if (isAbsoluteUrl(imageFilename)) {
    return imageFilename
  }

  // eslint-disable-next-line prefer-const
  let [filenameBase, fileExtension] = imageFilename.split('.')

  if (!fileExtension) {
    imageFilename = imageFilename + '.jpg'
    fileExtension = 'jpg'
  }
  const slug = slugify(textToCreateSlugFrom)
  const filename = slug ? `${slug}.${fileExtension}` : imageFilename

  // TODO: Fix media server to allow tiff images without dimensions
  // Show original unresized image unless tiff format
  return fileExtension !== 'tif' && fileExtension !== 'tiff'
    ? `image/${filenameBase}/${filename}`
    : `image/${filenameBase}/${xSize}/${ySize}/${filename}`
}

export const getRelativeImageUrl = getRelativeProductImageUrl
